/**
 * The browser-side half of validation, for the two anonymous forms this site still hosts:
 * subscribing and reporting a deal.
 *
 * Core validates everything again and its answer always wins — canon `docs/SECURITY.md`
 * treats browser input as untrusted, and a check that runs in a bundle anyone can edit is
 * not a security control. These functions exist for a different reason: to answer in the
 * time it takes to leave a field, instead of spending a round trip to say something the
 * page already knew.
 *
 * The rules mirror `dealbid-core`'s `src/lib/validation.ts` closely enough that a form
 * rarely disagrees with the server, and where they do differ these are the *looser* of the
 * two. A browser check that refused something core accepts would be this repo quietly
 * inventing product rules.
 *
 * The submission form's checks left with the form (`WEB-014`): canon decision `D-008` moved
 * submission into the signed-in seller app, and `dealbid-app` validates its own fields
 * against the same core rules. What remains here is what the two remaining forms use — a
 * second copy kept warm for a form this site does not show would drift out of step
 * unobserved.
 */

/**
 * Dot-atom local part plus a dotted domain with a final alphabetic label — the same shape
 * core accepts, and the same one the `subscribers.email` / `sellers.email` CHECK demands.
 */
const EMAIL_PATTERN =
  /^[a-z0-9!#$%&'*+/=?^_`{|}~-]+(?:\.[a-z0-9!#$%&'*+/=?^_`{|}~-]+)*@(?:[a-z0-9](?:[a-z0-9-]*[a-z0-9])?\.)+[a-z]{2,}$/;

/** A message when the value is unusable, or `null` when it is fine. */
export type FieldCheck = string | null;

/**
 * Not exported: `optionalText` is the only caller left now that the submission form's
 * required fields have gone to `dealbid-app` (`WEB-014`).
 */
function requiredText(value: string, max: number): FieldCheck {
  const trimmed = value.trim();
  if (trimmed === "") return "This field is required.";
  if (trimmed.length > max) return `Must be at most ${String(max)} characters.`;
  return null;
}

export function optionalText(value: string, max: number): FieldCheck {
  return value.trim() === "" ? null : requiredText(value, max);
}

export function email(value: string, options: { required: boolean }): FieldCheck {
  const trimmed = value.trim();
  if (trimmed === "") return options.required ? "This field is required." : null;
  if (trimmed.length > 254) return "Must be at most 254 characters.";
  return EMAIL_PATTERN.test(trimmed.toLowerCase())
    ? null
    : "Enter a valid email address.";
}

/** Normalised value to send: trimmed, or `null` when the field was left empty. */
export function optionalValue(value: string): string | null {
  const trimmed = value.trim();
  return trimmed === "" ? null : trimmed;
}
